import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Users, Eye, TrendingUp, Calendar, Clock, Activity, RefreshCw } from 'lucide-react';
import { TrailerStats } from './TrailerStats';

const API = process.env.REACT_APP_BACKEND_URL;

export const AnalyticsDashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load analytics');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAnalytics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  if (loading && !data) {
    return (
      <div className="bg-slate-800 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-slate-700 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="h-24 bg-slate-700 rounded"></div>
          <div className="h-24 bg-slate-700 rounded"></div>
          <div className="h-24 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800 rounded-xl p-6 border border-red-500/30">
        <p className="text-red-400">{error}</p>
        <button onClick={fetchAnalytics} className="mt-2 text-sm text-blue-400 hover:underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="analytics-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-bold text-white">Site Analytics</h2>
        </div>
        <button 
          onClick={fetchAnalytics}
          disabled={loading}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Today's Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-blue-400" />
            <span className="text-slate-400 text-sm">Today's Views</span>
          </div>
          <p className="text-2xl font-bold text-white" data-testid="today-views">{data?.today?.page_views || 0}</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-emerald-400" />
            <span className="text-slate-400 text-sm">Unique Visitors</span>
          </div>
          <p className="text-2xl font-bold text-white" data-testid="today-visitors">{data?.today?.unique_visitors || 0}</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-purple-400" />
            <span className="text-slate-400 text-sm">Active Subscribers</span>
          </div>
          <p className="text-2xl font-bold text-white" data-testid="active-subscribers">{data?.today?.active_subscribers || 0}</p>
        </div>
      </div>

      {/* Weekly Stats */}
      <TrailerStats trailer={data?.trailer} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-amber-400" />
            <span className="text-slate-400 text-sm">This Week</span>
          </div>
          <div className="flex justify-between">
            <div>
              <p className="text-lg font-bold text-white">{data?.week?.page_views || 0}</p>
              <p className="text-xs text-slate-500">Page Views</p>
            </div>
            <div>
              <p className="text-lg font-bold text-white">{data?.week?.unique_visitors || 0}</p>
              <p className="text-xs text-slate-500">Unique Visitors</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <span className="text-slate-400 text-sm">This Month</span>
          </div>
          <p className="text-lg font-bold text-white">{data?.month?.page_views || 0} <span className="text-sm text-slate-500">views</span></p>
        </div>
      </div>

      {/* Daily Breakdown Chart */}
      {data?.daily_breakdown && data.daily_breakdown.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-slate-400 mb-3">Last 7 Days</h3>
          <div className="flex items-end gap-2 h-24">
            {data.daily_breakdown.map((day, i) => {
              const maxViews = Math.max(...data.daily_breakdown.map(d => d.views), 1);
              const heightPx = Math.max((day.views / maxViews) * 80, 4); // Min 4px height
              return (
                <div key={i} className="flex-1 flex flex-col items-center h-full justify-end">
                  <div 
                    className="w-full bg-emerald-500/30 hover:bg-emerald-500/50 rounded-t transition-colors"
                    style={{ height: `${heightPx}px` }}
                    title={`${day.views} views, ${day.visitors} visitors`}
                  ></div>
                  <span className="text-xs text-slate-500 mt-1">{day.date}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Pages */}
      {data?.top_pages && data.top_pages.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-slate-400 mb-3">Top Pages</h3>
          <div className="space-y-2">
            {data.top_pages.slice(0, 5).map((page, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-300 truncate flex-1">{page.page}</span>
                <span className="text-slate-500 ml-2">{page.views} views</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Logins */}
      {data?.recent_logins && data.recent_logins.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-3">Recent Subscriber Activity</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data.recent_logins.slice(0, 10).map((login, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-slate-900/30 rounded p-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-slate-500" />
                  <span className="text-slate-300">{login.name || login.email}</span>
                </div>
                <span className="text-slate-500 text-xs">
                  {new Date(login.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
